// 스킬 트리 연출(docs/SKILL-TREE.md 4~5장, 시안 concept/skill-tree-20261020/vfx-*.webp).
// - 진홍 초승달(Ev.Slash b=14) + 쌍월(SkillBurst 4)
// - 랭크 5·캡스톤 추가 효과 6종(Ev.SkillBurst): 핏빛 궤적·폭풍의 눈·여진·쌍월·진홍 개화·뇌명 검기
// - 레벨 업(Ev.LevelUp): 영웅을 따라가는 금빛 광주·룬 고리(약 1.2초) + 올라가는 불꽃 + 고리 한 번
// 피해·데미지 숫자·타격 불똥·히트스톱·카메라 반응은 같은 틱 Ev.Hit이 이미 낸다(여기서 중복하지 않는다).
// 시안은 밝기·크기가 허용치보다 크다: 강도를 낮추고 바닥 흔적은 짧게 지워, 혼잡 웨이브에서도 영웅·적 실루엣과 바닥 절반 이상이 읽히게 한다.
// 스프라이트는 CombatFx의 VfxSystem 묶음(드로우콜 = 쓰는 텍스처 수)을 쓰고, 영웅을 따라다니는 광주·고리만 전용 사각형 2장이다.
// 프레임 루프·이벤트 처리 안에서 새 객체를 만들지 않는다(풀·큐를 쓴다). 전체 화면 섬광은 쓰지 않으므로 FlashLimiter를 거치지 않는다.
import * as THREE from 'three/webgpu';
import { abs, float, max, sin, smoothstep, texture, uniform, uv, vec3 } from 'three/tsl';
import { BurstKind, CRESCENT } from '../shared/growth';
import type { GameEvent } from '../shared/events';
import { SPEC } from './lib/specs';
import { newBurst, type Burst, type VfxSystem } from './lib/system';
import type { TexId } from './lib/textures';
import type { ParticleFx } from './particles';

const TAU = Math.PI * 2;
/** 불똥 색(HDR) */
const CRIMSON = [2.4, 0.3, 0.42] as const;
const AMBER = [2.5, 1.45, 0.35] as const;
const BLUE = [1.0, 1.6, 2.9] as const;
const GOLD = [2.4, 1.7, 0.5] as const;
const STONE = 0x7d6c58;
/** 레벨 업 표시 시간(초)과 단계 */
const LU = { dur: 1.25, fadeIn: 0.14, fadeOut: 0.5, pillarH: 7.5, pillarW: 4.2, ringD: 6.0, spin: 1.5, sparkEvery: 0.1, sparks: 6, columnW: 3.0 } as const;

/** 영웅을 따라다니는 가산 사각형(광주·룬 고리). 텍스처는 로드가 끝난 뒤 bind로 끼운다 */
class FollowQuad {
  readonly mesh: THREE.Mesh;
  readonly opacity = uniform(0);
  /** 섬광 약화에서 줄이는 빛 배율 */
  readonly gain = uniform(1);
  private readonly map: { value: THREE.Texture };

  constructor(initial: THREE.Texture, ground: boolean, tint: number, column = false) {
    const geo = new THREE.PlaneGeometry(1, 1);
    if (ground) geo.rotateX(-Math.PI / 2);
    const t = texture(initial, uv());
    this.map = t as unknown as { value: THREE.Texture };
    const c = new THREE.Color(tint);
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
    m.fog = false;
    m.forceSinglePass = true;
    if (column) {
      // 넓은 금빛 기둥: 좌우로 부드럽게 꺼지고(가장자리 0), 위로 갈수록 흐려진다(바닥에서 가장 밝다). 가는 세로 결을 살짝 얹는다
      const x = uv().x, y = uv().y;
      // 가장자리는 넓게 켜지고(0.9승), 세로로는 밑동에서 0.55 → 머리 높이(밑에서 18%)에서 최대 → 위로 서서히 꺼진다
      const edge = float(1).sub(abs(x.mul(2).sub(1))).pow(1.2);
      const vf = float(1).sub(y).pow(0.9).mul(smoothstep(float(0), float(0.18), y).mul(0.45).add(0.55));
      const grain = sin(x.mul(46)).mul(0.1).add(0.9);
      m.colorNode = vec3(c.r, c.g, c.b).mul(edge.mul(vf).mul(grain)).mul(1.35).mul(this.gain);
    } else m.colorNode = max(t.rgb.sub(0.03), vec3(0)).mul(vec3(c.r, c.g, c.b)).mul(this.gain);
    m.opacityNode = this.opacity;
    this.mesh = new THREE.Mesh(geo, m);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2;
    this.mesh.visible = false;
  }

  bind(t: THREE.Texture): void { this.map.value = t; }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}

export class TreeFx {
  readonly group = new THREE.Group();
  /** 품질 배율(CombatFx.setScale와 같은 값)과 섬광 약화 */
  scale = 1;
  private reduce = false;
  private readonly pillar: FollowQuad;
  /** 광주 중심의 가는 빛줄기(텍스처) */
  private readonly core: FollowQuad;
  private readonly ring: FollowQuad;
  private readonly B: Burst = newBurst();
  private seed = 7;
  // 레벨 업 진행(영웅 시계 틱)
  private luOn = false;
  private luStart = 0;
  private luNextSpark = 0;
  /** 초승달이 날아가는 동안(영웅 시계 틱까지)의 스킬 타격은 적 몸에 진홍 섬광을 낸다 */
  private waveUntil = -1;
  private prewarming = false;
  /** 마지막 카메라 위치(광주를 영웅 뒤로 밀 때) */
  private camX = 0; private camZ = 20;
  /** QA 읽기: 마지막으로 낸 효과 이름과 횟수 */
  readonly count = { crescent: 0, burst: 0, levelUp: 0 };

  constructor(private readonly vfx: VfxSystem, private readonly particles: ParticleFx) {
    this.group.name = 'tree-fx';
    const blank = vfx.bank.blank;
    this.pillar = new FollowQuad(blank, false, 0xffd57a, true);
    this.core = new FollowQuad(blank, false, 0xffe9b0);
    this.ring = new FollowQuad(blank, true, 0xffffff);
    this.group.add(this.pillar.mesh, this.core.mesh, this.ring.mesh);
  }

  /** 사용할 텍스처 목록(VfxSystem.load 추가분). 광주·룬 고리는 스프라이트 묶음 밖이다 */
  static readonly EXTRA_TEXTURES: readonly TexId[] = ['levelup-pillar', 'rune-ring-gold'];

  /** 텍스처 로드가 끝난 뒤 한 번 */
  bind(): void {
    const p = this.vfx.bank.get('levelup-pillar'), r = this.vfx.bank.get('rune-ring-gold');
    if (p) this.core.bind(p);
    if (r) this.ring.bind(r);
  }

  setReduce(on: boolean): void {
    this.reduce = on;
    this.pillar.gain.value = on ? 0.5 : 1;
    this.core.gain.value = on ? 0.5 : 1;
    this.ring.gain.value = on ? 0.6 : 1;
  }

  private rnd(): number { this.seed = (this.seed * 16807) % 2147483647; return this.seed / 2147483647; }
  private n(v: number): number { return Math.round(v * this.scale); }

  private streaks(delay: number, x: number, y: number, z: number, dir: number, spread: number, count: number, speed: number, c: readonly [number, number, number], white: number, life: number, up: number): void {
    const b = this.B;
    b.x = x; b.y = y; b.z = z; b.dir = dir; b.spread = spread; b.count = this.n(count); b.speed = speed; b.r = c[0]; b.g = c[1]; b.b = c[2];
    b.white = this.reduce ? white * 0.4 : white; b.life = life; b.up = up;
    if (delay > 0) this.vfx.laterBurst(delay, b); else this.vfx.burst(b);
  }

  // ── 진홍 초승달 ─────────────────────────────────────

  /**
   * Ev.Slash b=14: x,z 영웅 위치, a 방향, c 길이(8m), d 폭(2.4m).
   * 초승달 본체가 길이를 0.3초에 날아가고, 뒤에 어두운 잔상 둘, 바닥에 얕은 긁힌 자국, 끝점에 작은 불꽃.
   */
  crescent(e: GameEvent, now: number, scar = true): void {
    const f = e.a, sx = Math.sin(f), cz = Math.cos(f);
    const len = e.c > 0 ? e.c : CRESCENT.length;
    const w = e.d > 0 ? e.d : CRESCENT.width;
    const k = w / 2.4;
    this.count.crescent++;
    this.waveUntil = now + 24;
    // 본체(밝은 앞날) + 짙은 진홍 몸통을 겹쳐 두께를 낸다
    this.vfx.sprite(SPEC.crescentWave, e.x + sx * 0.9, 1.2, e.z + cz * 0.9, f, k, -0.25);
    this.vfx.sprite(SPEC.crescentBody, e.x + sx * 0.8, 1.15, e.z + cz * 0.8, f, k, -0.25);
    if (!this.reduce) {
      this.vfx.later(0.035, SPEC.crescentGhost, e.x + sx * 0.9, 1.1, e.z + cz * 0.9, f, k, -0.25);
      this.vfx.later(0.07, SPEC.crescentGhost, e.x + sx * 0.9, 1.0, e.z + cz * 0.9, f, k, -0.25);
    }
    // 바닥 균열: 두 겹(넓은 층 + 좁고 밝은 심)으로 두껍게, 영웅 앞 0.6m부터 끝까지
    // scar = false면 VFX 모듈 균열(crescent_wave)이 대신 그린다
    if (scar) {
      const cx = e.x + sx * (len * 0.5 + 0.6), cy = e.z + cz * (len * 0.5 + 0.6);
      this.vfx.sprite(SPEC.scarCrimson, cx, 0.04, cy, f, len, 0, false, 1.0 * k);
      this.vfx.sprite(SPEC.scarCrimson, cx, 0.045, cy, f, len, 0, false, 0.5 * k);
    }
    // 끝점 불꽃(도착 순간)
    const ex = e.x + sx * (len + 0.4), ez = e.z + cz * (len + 0.4);
    this.vfx.later(CRESCENT.travelTicks / 60, SPEC.flareSmall, ex, 0.7, ez, 0, this.reduce ? 0.5 : 1.0, this.rnd() * Math.PI);
    this.streaks(CRESCENT.travelTicks / 60, ex, 0.6, ez, f, 2.4, 14, 7, CRIMSON, 0.4, 0.28, 2.2);
  }

  /** 초승달이 날아가는 동안인가(그 사이의 스킬 타격에 진홍 섬광을 낸다) */
  waveLive(now: number): boolean { return now <= this.waveUntil; }

  // ── 랭크 5·캡스톤 추가 효과 ─────────────────────────

  /** Ev.SkillBurst: x,z 중심(선분형은 시작점), a 종류, b 반경 또는 길이, c 방향, d 폭 */
  burst(e: GameEvent, now: number): void {
    this.count.burst++;
    const r = e.b > 0 ? e.b : 1;
    switch (e.a | 0) {
      case BurstKind.DashWake: this.dashWake(e.x, e.z, r, e.c); break;
      case BurstKind.StormEye: this.stormEye(e.x, e.z, r); break;
      case BurstKind.Aftershock: this.aftershock(e.x, e.z, r); break;
      case BurstKind.TwinMoon: this.twinMoon(e.x, e.z, r, e.c, e.d, now); break;
      case BurstKind.Bloom: this.bloom(e.x, e.z, r); break;
      case BurstKind.Thunder: this.thunder(e.x, e.z, r, e.c, e.d); break;
      default: break;
    }
  }

  /** 1 핏빛 궤적: 돌진 경로에 진홍 균열이 터지듯 그려지고 짧은 가시가 시작에서 끝으로 차례로 솟는다 */
  private dashWake(x: number, z: number, len: number, f: number): void {
    const sx = Math.sin(f), cz = Math.cos(f);
    this.vfx.sprite(SPEC.scarDash, x + sx * len * 0.5, 0.04, z + cz * len * 0.5, f, len, 0, false, 0.5);
    const n = Math.max(3, Math.min(9, Math.round(len / 0.9)));
    const free = this.vfx.free(SPEC.spikeCrimson);
    for (let i = 0; i < n && i < free; i++) {
      const t = 0.8 + ((i + 0.5) / n) * (len - 0.8);
      const side = (i & 1 ? 0.3 : -0.3) + (this.rnd() - 0.5) * 0.3;
      const px = x + sx * t + cz * side, pz = z + cz * t - sx * side;
      this.vfx.later(0.02 + i * 0.028, SPEC.spikeCrimson, px, 0.5, pz, 0, 0.75 + this.rnd() * 0.45, (this.rnd() - 0.5) * 0.4);
    }
    // 돌조각·불똥: 경로 앞·가운데·끝 세 곳
    for (let k = 0; k < 3; k++) {
      const t = len * (0.2 + 0.35 * k);
      this.particles.debris(x + sx * t, z + cz * t, f, this.n(3), STONE);
      this.streaks(0.03 * k * 3, x + sx * t, 0.5, z + cz * t, f, 2.2, 6, 6, CRIMSON, 0.35, 0.3, 3);
    }
    if (this.vfx.free(SPEC.dust) > 6) {
      this.vfx.sprite(SPEC.dust, x + sx * len * 0.3, 0.2, z + cz * len * 0.3, 0, 0.6, this.rnd() * TAU);
      this.vfx.sprite(SPEC.dust, x + sx * len * 0.7, 0.2, z + cz * len * 0.7, 0, 0.6, this.rnd() * TAU);
    }
  }

  /** 2 폭풍의 눈: 소용돌이가 한 점으로 모였다가(0.3초) 반경까지 한 겹 터진다 */
  private stormEye(x: number, z: number, r: number): void {
    this.vfx.sprite(SPEC.eyeVortex, x, 0.05, z, 0, r * 0.78, this.rnd() * TAU);
    this.vfx.sprite(SPEC.flareSmall, x, 0.6, z, 0, this.reduce ? 0.7 : 1.2, this.rnd() * Math.PI);
    this.vfx.later(0.3, SPEC.eyeRing, x, 0.06, z, 0, r);
    this.vfx.later(0.36, SPEC.eyeRing, x, 0.07, z, 0, r * 0.6);
    this.vfx.later(0.3, SPEC.flareSmall, x, 0.9, z, 0, this.reduce ? 0.7 : 1.3, this.rnd() * Math.PI);
    this.streaks(0.3, x, 0.7, z, 0, TAU, 40, 11, CRIMSON, 0.5, 0.4, 3);
    this.particles.debris(x, z, 0, this.n(6), STONE);
  }

  /** 3 여진: 금빛 호박색 고리가 퍼지고 중심에서 방사 균열이 바깥으로 그려진다 */
  private aftershock(x: number, z: number, r: number): void {
    this.vfx.sprite(SPEC.flareSmall, x, 0.7, z, 0, this.reduce ? 0.8 : 1.6, this.rnd() * Math.PI);
    this.vfx.sprite(SPEC.ringAmber, x, 0.06, z, 0, r);
    this.vfx.later(0.06, SPEC.ringAmber, x, 0.07, z, 0, r * 0.62);
    const N = 8, len = r * 0.9;
    const a0 = this.rnd() * TAU;
    for (let i = 0; i < N; i++) {
      const a = a0 + (i / N) * TAU + (this.rnd() - 0.5) * 0.22;
      const c = len * 0.5 + 0.5;
      this.vfx.sprite(SPEC.crackAmber, x + Math.sin(a) * c, 0.045, z + Math.cos(a) * c, a, len, 0, false, 0.32);
    }
    this.streaks(0, x, 0.5, z, 0, TAU, 34, 9, AMBER, 0.35, 0.45, 2.6);
    this.particles.debris(x, z, 0, this.n(8), STONE);
    if (this.vfx.free(SPEC.dust) > 10) {
      for (let i = 0; i < 4; i++) {
        const a = a0 + (i / 4) * TAU + 0.4;
        this.vfx.sprite(SPEC.dust, x + Math.sin(a) * r * 0.55, 0.25, z + Math.cos(a) * r * 0.55, 0, 0.7, i);
      }
    }
  }

  /**
   * 4 쌍월: 판정과 같은 길(이벤트 원점·facing 그대로, 폭 2.4m)로 두 번째 초승달을 낸다.
   * X로 엇갈려 보이는 것은 경로 밖으로 나가지 않는 표현만 쓴다: 초승달 자체를 첫째와 반대로 눕혀 좌우 반전(스핀 ±0.25rad)하고,
   * 바닥 균열은 길 가운데를 축으로 ±0.14rad만 돌려 첫째 균열과 교차시킨다(끝에서도 가운데 선에서 0.6m 이내).
   */
  private twinMoon(x: number, z: number, len: number, f: number, w: number, now: number): void {
    const k = w > 0 ? w / 2.4 : 1;
    this.waveUntil = now + 24;
    const sx = Math.sin(f), cz = Math.cos(f);
    this.vfx.sprite(SPEC.crescentTwin, x + sx * 0.9, 1.1, z + cz * 0.9, f, k * 0.92, 0.25, true);
    this.vfx.sprite(SPEC.crescentBody, x + sx * 0.8, 1.05, z + cz * 0.8, f, k * 0.85, 0.25, true);
    if (!this.reduce) this.vfx.later(0.04, SPEC.crescentGhost, x + sx * 0.9, 1.0, z + cz * 0.9, f, k * 0.85, 0.25, true);
    // 길 가운데(원점에서 0.6m + 길이/2)를 축으로 돌린 균열: 첫째 균열과 길 가운데에서 X로 만난다
    const mid = len * 0.5 + 0.6, cx = x + sx * mid, cy = z + cz * mid;
    this.vfx.sprite(SPEC.scarCrimson, cx, 0.05, cy, f + 0.14, len * 0.95, 0, true, 1.0 * k);
    this.vfx.sprite(SPEC.scarCrimson, cx, 0.055, cy, f - 0.14, len * 0.95, 0, false, 0.5 * k);
    const ex = x + sx * (len + 0.2), ez = z + cz * (len + 0.2);
    this.vfx.later(CRESCENT.travelTicks / 60, SPEC.flareSmall, ex, 0.7, ez, 0, this.reduce ? 0.45 : 0.9, this.rnd() * Math.PI);
    this.streaks(CRESCENT.travelTicks / 60, ex, 0.6, ez, f, 2.4, 12, 7, CRIMSON, 0.4, 0.26, 2.2);
  }

  /** 5 진홍 개화: 바닥에 꽃잎·칼날 조각이 퍼지고 룬 꽃 데칼이 잠깐 켜지며 위로 꽃잎 불똥이 번진다 */
  private bloom(x: number, z: number, r: number): void {
    this.vfx.sprite(SPEC.bloomGround, x, 0.07, z, 0, r * 1.5, this.rnd() * TAU);
    this.vfx.sprite(SPEC.runeBloom, x, 0.05, z, 0, r * 0.9);
    this.vfx.later(0.08, SPEC.eyeRing, x, 0.06, z, 0, r * 0.9);
    this.streaks(0, x, 0.6, z, 0, TAU, 56, 10, CRIMSON, 0.3, 0.55, 5.5);
    this.particles.debris(x, z, 0, this.n(8), STONE);
  }

  /** 6 뇌명 검기: 청백 번개 칼날이 b미터를 0.18초에 나가고 지면에 짧은 번개 호가 남는다 */
  private thunder(x: number, z: number, len: number, f: number, w: number): void {
    const sx = Math.sin(f), cz = Math.cos(f);
    const k = Math.max(0.8, (w > 0 ? w : 1.6) / 1.6) * 1.2;
    this.vfx.sprite(SPEC.thunderBlade, x + sx * 0.8, 0.5, z + cz * 0.8, f, k);
    this.vfx.sprite(SPEC.flareSmall, x + sx * 0.9, 0.8, z + cz * 0.9, 0, this.reduce ? 0.7 : 1.3, this.rnd() * Math.PI);
    if (!this.reduce) {
      this.vfx.later(0.03, SPEC.thunderGhost, x + sx * 0.8, 0.45, z + cz * 0.8, f, k);
      this.vfx.later(0.06, SPEC.thunderGhost, x + sx * 0.8, 0.4, z + cz * 0.8, f, k);
    }
    const speed = SPEC.thunderBlade.move;
    for (let i = 0; i < 4; i++) {
      const t = len * (0.28 + 0.24 * i);
      const side = (this.rnd() - 0.5) * w * 0.5;
      this.vfx.later(t / speed, SPEC.arcBlue, x + sx * t + cz * side, 0.05, z + cz * t - sx * side, f + (this.rnd() - 0.5) * 2.4, 1.5 + this.rnd() * 0.7, 0, false, 0.9);
    }
    this.streaks(0, x + sx * 0.8, 0.7, z + cz * 0.8, f, 1.2, 8, 8, BLUE, 0, 0.25, 1.5);
    this.streaks(len / speed, x + sx * len, 0.6, z + cz * len, f, 2.4, 14, 8, BLUE, 0, 0.3, 2.2);
  }

  // ── 레벨 업 ─────────────────────────────────────────

  /** Ev.LevelUp: 금빛 광주 + 돌아가는 금빛 룬 고리(영웅을 따라간다) + 올라가는 불꽃 + 고리 한 번 */
  levelUp(e: GameEvent, now: number): void {
    this.count.levelUp++;
    this.luOn = true;
    this.luStart = now;
    this.luNextSpark = now;
    // 고리 박동: 겹쳐 두껍게 한 번, 조금 뒤 더 큰 한 겹 더
    this.vfx.sprite(SPEC.ringLevel, e.x, 0.08, e.z, 0, 4.8);
    this.vfx.sprite(SPEC.ringLevel, e.x, 0.085, e.z, 0, 4.4);
    this.vfx.later(0.18, SPEC.ringLevel, e.x, 0.09, e.z, 0, 6.0);
    this.streaks(0, e.x, 0.3, e.z, 0, TAU, 44, 3.4, GOLD, 0.25, 1.0, 7);
  }

  /** 매 프레임: 영웅 렌더 위치·카메라·영웅 시계(틱) */
  update(now: number, hx: number, hy: number, hz: number, camX: number, camZ: number): void {
    this.camX = camX; this.camZ = camZ;
    if (!this.luOn) return;
    // 이벤트 시각(heroAt)이 렌더 보간 때문에 이번 프레임 시계보다 한 틱 앞설 수 있다: 음수는 0으로 맞춘다(예전에는 바로 꺼졌다)
    const t = Math.max(0, (now - this.luStart) / 60);
    if (t >= LU.dur) { this.luOn = false; this.clear(); return; }
    const a = t < LU.fadeIn ? t / LU.fadeIn : 1;
    const o = t > LU.dur - LU.fadeOut ? (LU.dur - t) / LU.fadeOut : 1;
    const k = a * o;
    // 광주는 영웅 뒤(카메라 반대쪽)에 세워 영웅이 앞에서 가린다
    let dx = hx - camX, dz = hz - camZ;
    const l = Math.hypot(dx, dz) || 1;
    dx /= l; dz /= l;
    const p = this.pillar, c = this.core;
    const grow = 0.6 + 0.4 * (1 - (1 - Math.min(1, t / 0.25)) ** 3);
    const bx = hx + dx * 0.35, bz = hz + dz * 0.35, yaw = Math.atan2(camX - hx, camZ - hz);
    p.mesh.visible = true;
    p.mesh.position.set(bx, hy + LU.pillarH * 0.5 - 0.3, bz);
    p.mesh.rotation.set(0, yaw, 0);
    p.mesh.scale.set(LU.columnW * grow, LU.pillarH, 1);
    p.opacity.value = 0.8 * k;
    c.mesh.visible = true;
    c.mesh.position.set(bx, hy + LU.pillarH * 0.5 - 0.45, bz);
    c.mesh.rotation.set(0, yaw, 0);
    c.mesh.scale.set(LU.pillarW * grow, LU.pillarH, 1);
    c.opacity.value = 0.55 * k;
    const r = this.ring;
    const rs = 0.55 + 0.45 * (1 - (1 - Math.min(1, t / 0.35)) ** 3);
    r.mesh.visible = true;
    r.mesh.position.set(hx, hy + 0.07, hz);
    r.mesh.rotation.set(0, t * LU.spin, 0);
    r.mesh.scale.set(LU.ringD * rs, 1, LU.ringD * rs);
    r.opacity.value = 0.85 * k;
    // 올라가는 불꽃(영웅 둘레)
    if (now >= this.luNextSpark && !this.reduce) {
      this.luNextSpark = now + LU.sparkEvery * 60;
      const ang = this.rnd() * TAU, rad = 0.5 + this.rnd() * 1.6;
      this.streaks(0, hx + Math.sin(ang) * rad, hy + 0.2, hz + Math.cos(ang) * rad, 0, TAU, LU.sparks, 0.8, GOLD, 0.2, 0.8, 5);
    }
  }

  /** 미리 컴파일: 광주·고리를 불투명도 0으로 한 프레임 보이게 둔다 */
  prewarm(on: boolean): void {
    this.prewarming = on;
    for (const q of [this.pillar, this.core, this.ring]) {
      if (on) { q.opacity.value = 0; q.mesh.position.set(0, -50, 0); q.mesh.scale.set(0.01, 0.01, 0.01); q.mesh.visible = true; }
      else if (!this.luOn) q.mesh.visible = false;
    }
  }

  /** QA 읽기 */
  state(): { levelUp: boolean; pillar: number; ring: number; prewarming: boolean; camX: number; camZ: number } {
    return { levelUp: this.luOn, pillar: this.pillar.opacity.value as number, ring: this.ring.opacity.value as number, prewarming: this.prewarming, camX: this.camX, camZ: this.camZ };
  }

  clear(): void {
    this.luOn = false;
    for (const q of [this.pillar, this.core, this.ring]) { q.mesh.visible = false; q.opacity.value = 0; }
  }

  dispose(): void {
    this.pillar.dispose();
    this.ring.dispose();
  }
}
